import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { Toaster } from "sonner";

import PublicLayout from "@/components/PublicLayout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import Gallery from "@/pages/Gallery";
import Tables from "@/pages/Tables";
import Tickets from "@/pages/Tickets";
import Book from "@/pages/Book";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import StaffDashboard from "@/pages/StaffDashboard";
import LoyaltyBuy from "@/pages/LoyaltyBuy";
import LoyaltyCardPage from "@/pages/LoyaltyCardPage";
import BookingDetailsPage from "@/pages/BookingDetailsPage";
import MenuPage from "@/pages/Menu";
import MenuQrPoster from "@/pages/MenuQrPoster";
import NightSheet from "@/pages/NightSheet";
import NotFound from "@/pages/NotFound";

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505] text-neon">
        <span className="font-heading text-3xl tracking-widest">LOADING…</span>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LangProvider>
          <BrowserRouter>
            <ScrollToTop />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#0a0a0a",
                color: "#fff",
                border: "1px solid #FF1493",
                borderRadius: 0,
              },
            }}
          />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/events" element={<Events />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/tables" element={<Tables />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/drinks-tickets" element={<Tickets />} />
              {/* Back-compat redirects — old marketing links / bookmarks */}
              <Route path="/loyalty-cards" element={<Navigate to="/drinks-tickets" replace />} />
              <Route path="/loyalty" element={<Navigate to="/drinks-tickets" replace />} />
              <Route path="/book" element={<Book />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/loyalty/buy" element={<LoyaltyBuy />} />
            </Route>
            {/* Digital drink card — guest's phone view (no public layout chrome) */}
            <Route path="/card/:token" element={<LoyaltyCardPage />} />
            {/* Guest booking receipt — same standalone layout (no public chrome) */}
            <Route path="/booking/:id" element={<BookingDetailsPage />} />
            {/* Standalone in-venue drinks menu — bare layout, no header/footer */}
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/menu-qr" element={<MenuQrPoster />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/admin/night-sheet"
              element={
                <Protected roles={["admin", "staff"]}>
                  <NightSheet />
                </Protected>
              }
            />
            <Route
              path="/admin"
              element={
                <Protected roles={["admin", "staff"]}>
                  <StaffDashboard />
                </Protected>
              }
            />
            {/* Legacy /staff URL — redirect to the unified /admin Command Centre */}
            <Route path="/staff" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </LangProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
